import { useContext, useState } from 'react';
import { Box, Button, Grid, Typography, Divider, FormControlLabel } from '@mui/material';
import {
    AccountCircleOutlined,
    CalendarTodayOutlined,
    CheckCircle,
    CreditCard,
    Refresh,
    LocationOnOutlined
} from '@mui/icons-material';
// Our Components
import { AuthContext } from 'context/auth-context';
import { CARD_TYPE, REPLENISH_FRECUENCY, GIFT_CARD_CATEGORY } from 'helpers/constants';
import CVVSwitch from 'components/shared/CVVSwitch';
import PaystandCard from './PaystandCard/PaystandCard';
// Utils and Helpers
import { hasCreditLine, hasCreditModel } from 'helpers/businesses';
import { numberFormat } from 'helpers/i18n';
import dayjs from 'helpers/dayjs';
// Assets
import physicalCardBackside from 'assets/cardsSection/cardBackgroud.svg';
import virtualCardBackside from 'assets/createCards/virtualCardBackside.png';
import './CardOverview.css';

const CardOverview = ({ createdCard, card, onDoneClick }) => {
    const { businessInfo } = useContext(AuthContext);
    const [showCardInfo, setShowCardInfo] = useState(false);

    const getReplenishFrequency = replenishFrequency => {
        return replenishFrequency === REPLENISH_FRECUENCY.ONE_TIME ? 'Manual' : 'Automatic';
    };

    const getBusinessAddress = () => {
        const { address } = businessInfo;
        const lineTwo = !address.line2 ? '' : `${address.line2}, `;
        return `${address.line1}, ${lineTwo}${address.city}, ${address.state} ${address.postalCode}`;
    };

    const getTitleText = () => {
        if (card.type === CARD_TYPE.PHYSICAL && card.label === GIFT_CARD_CATEGORY) {
            return `Your order for ${GIFT_CARD_CATEGORY} is successful`;
        }
        if (card.type === CARD_TYPE.PHYSICAL) {
            return 'Your order is successful';
        }
        return `Your ${card.label === GIFT_CARD_CATEGORY ? GIFT_CARD_CATEGORY : 'card'} is available now!`;
    };

    const cardLabel = card.label === GIFT_CARD_CATEGORY ? GIFT_CARD_CATEGORY : 'card';

    return (
        <Box className="review-card" sx={{ width: '100%' }}>
            <Grid container columnSpacing={{ md: 3 }}>
                <Grid item md={6}>
                    <Box>
                        <Box className="review-card-overview__icon-container" sx={{ marginBottom: '16px' }}>
                            <CheckCircle sx={{ color: '#429E00', fontSize: '35px' }} />
                        </Box>
                        <Typography className="review-card-overview__congrats">
                            Congratulations, {card?.cardholder?.first_name}
                        </Typography>
                        <Typography className="review-card-overview__title">{getTitleText()}</Typography>
                        {card.type === CARD_TYPE.PHYSICAL && (
                            <>
                                <Typography className="review-card-overview__delivery-time" marginBottom="25px">
                                    Your physical card will be delivered to the below address in 5-7 business days. We will send
                                    you the UPS tracking number via email once the card is shipped.
                                </Typography>
                                <Box display="flex" alignItems="center">
                                    <LocationOnOutlined sx={{ marginRight: '14px' }} />
                                    <Typography component="span" className="review-card-overview__info">
                                        {getBusinessAddress()}
                                    </Typography>
                                </Box>
                            </>
                        )}
                        <Divider sx={{ marginTop: '15px', marginBottom: '15px' }} />
                        <Box sx={{ marginBottom: '35px' }}>
                            <Box display="flex" alignItems="center" marginBottom="16px">
                                <AccountCircleOutlined sx={{ marginRight: '14px' }} />
                                <Typography component="span" className="review-card-overview__info">
                                    Cardholder: {`${card?.cardholder?.first_name} ${card?.cardholder?.last_name}`}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" marginBottom="16px">
                                <CreditCard sx={{ marginRight: '14px' }} />
                                <Typography component="span" className="review-card-overview__info">
                                    {hasCreditModel(businessInfo) && hasCreditLine(businessInfo)
                                        ? `Limit: ${numberFormat(card.limitAmount)}`
                                        : `Card Balance: ${numberFormat(card.limitAmount)}`}
                                </Typography>
                            </Box>
                            {hasCreditModel(businessInfo) && hasCreditLine(businessInfo) && (
                                <Box display="flex" alignItems="center" marginBottom="16px">
                                    <Refresh sx={{ marginRight: '14px' }} />
                                    <Typography component="span" className="review-card-overview__info">
                                        Replenishment: {getReplenishFrequency(card.has_manual_replenish)}
                                    </Typography>
                                </Box>
                            )}
                            <Box display="flex" alignItems="center" marginBottom="35px">
                                <CalendarTodayOutlined sx={{ marginRight: '14px' }} />
                                <Typography component="span" className="review-card-overview__info">
                                    Lock Date: {card.autoLock ? dayjs(card.autoLock).format('DD/MM/YYYY') : 'N/A'}
                                </Typography>
                            </Box>
                        </Box>
                        <Button variant="contained" onClick={onDoneClick} sx={{ width: '100%' }}>
                            Done
                        </Button>
                    </Box>
                </Grid>
                <Grid item md={6}>
                    {(card.type === CARD_TYPE.PHYSICAL || cardLabel === GIFT_CARD_CATEGORY) && (
                        <Box
                            className="review-card__card-banner-container"
                            sx={{
                                background: 'linear-gradient(to bottom, rgba(27, 171, 226, 0.2) 50%, #f9f9f8 50%)'
                            }}>
                            <Box className="review-card__card-image-container-overview" sx={{ position: 'relative' }}>
                                <img
                                    className="review-card__card-image"
                                    src={card.type === CARD_TYPE.VIRTUAL ? virtualCardBackside : physicalCardBackside}
                                    alt="virtual card"
                                />
                                <Typography component="div" className="review-card__card-secret review-card__card-number">
                                    **** **** ****{' '}
                                    <span
                                        style={{
                                            position: 'absolute',
                                            top: -2,
                                            left: 90,
                                            fontSize: '14px'
                                        }}>
                                        {`  ${createdCard?.solidData?.last4}`}
                                    </span>
                                </Typography>
                                <Typography component="div" className="review-card__card-secret review-card__card-expiration">
                                    **/**
                                </Typography>
                                <Typography component="div" className="review-card__card-secret review-card__card-cvv">
                                    ***
                                </Typography>

                                <Typography
                                    component="div"
                                    className="review-card__card-backside-cardholder-name embossing-ellipsis"
                                    sx={{
                                        color: `${card.type === CARD_TYPE.VIRTUAL ? 'var(--white-color)' : 'var(--main-color)'}`
                                    }}>
                                    {`${card?.cardholder?.first_name} ${card?.cardholder?.last_name}`}
                                </Typography>
                                <Typography
                                    component="div"
                                    className="review-card__card-backside-cardholder-company embossing-ellipsis"
                                    sx={{
                                        color: `${card.type === CARD_TYPE.VIRTUAL ? 'var(--white-color)' : 'var(--main-color)'}`
                                    }}>
                                    {businessInfo.legal_name}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {card.type === CARD_TYPE.VIRTUAL && cardLabel !== GIFT_CARD_CATEGORY && (
                        <Box
                            className="review-card__card-banner-container"
                            sx={{
                                background: 'linear-gradient(to bottom, rgba(27, 171, 226, 0.2) 50%, #f9f9f8 50%)',
                                flexDirection: 'column'
                            }}>
                            <FormControlLabel
                                control={<CVVSwitch card={createdCard} checked={showCardInfo} setChecked={setShowCardInfo} />}
                                label={showCardInfo ? 'Hide Card Details' : 'Show Card Details'}
                            />
                            <Box className="review-card__card-image-container-overview" marginBottom="0px">
                                <PaystandCard
                                    card={createdCard?.solidData}
                                    showCardInfo={showCardInfo}
                                    setShowCardInfo={setShowCardInfo}
                                />
                            </Box>
                        </Box>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

export default CardOverview;
